import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getLessonSets, getCustomSets, LessonSet, Lesson, CustomSet } from '../services/api';
import './Games.css';

interface GameModule {
  id: string;
  name: string;
  description: string;
  icon: string;
  path: string;
}

const gameModules: GameModule[] = [
  { id: 'flashcard', name: 'Quiz Fiszek', description: 'Wybierz poprawne tłumaczenie', icon: '🃏', path: '/games/flashcard' },
  { id: 'listening', name: 'Słuchanie', description: 'Posłuchaj i wybierz słowo', icon: '👂', path: '/games/listening' },
  { id: 'matching', name: 'Dopasowywanie', description: 'Połącz słowa z tłumaczeniami', icon: '🔗', path: '/games/matching' },
  { id: 'spelling', name: 'Pisownia', description: 'Wpisz słowo po angielsku', icon: '✏️', path: '/games/spelling' },
  { id: 'sentence', name: 'Ułóż zdanie', description: 'Ułóż słowa w poprawnej kolejności', icon: '📝', path: '/games/sentence' },
  { id: 'anagram', name: 'Anagram', description: 'Ułóż litery w poprawnej kolejności', icon: '🔤', path: '/games/anagram' },
  { id: 'fillgap', name: 'Uzupełnij luki', description: 'Wybierz brakujące słowo w zdaniu', icon: '🧩', path: '/games/fillgap' },
  { id: 'dictation', name: 'Dyktando', description: 'Posłuchaj i napisz co słyszysz', icon: '🎧', path: '/games/dictation' }
];

// Lesson icons - synced with Lessons.tsx
const lessonIcons: Record<string, string> = {
  'Owoce': '🍎',
  'Warzywa': '🥕',
  'Jedzenie i napoje': '🍕',
  'Czynności - jedzenie': '🍴',
  'Zwierzęta domowe': '🐕',
  'Zwierzęta dzikie': '🦁',
  'Zwierzęta morskie': '🐠',
  'Czynności - zwierzęta': '🐾',
  'Rodzina': '👨‍👩‍👧',
  'Zawody i ludzie': '👷',
  'Części ciała': '🦵',
  'Czynności - własne ciało': '🤸',
  'Czynności - inni ludzie': '🤝',
  'Ubrania': '👕',
  'Ubrania i czynności': '👔',
  'Transport': '🚗',
  'Transport i czynności': '🚌',
  'Przedmioty codzienne': '🎒',
  'Dom i wyposażenie': '🛋️',
  'Dom i przedmioty': '🏠',
  'Ogród': '🌻',
  'Czynności - dom i ogród': '🧹',
  'Kształty': '⭐',
  'Sport': '⚽',
  'Poruszanie się': '🚶',
  'Wykonywanie pracy': '💼',
  'Geografia - podstawy': '🌍',
  'Geografia - trudniejsze': '🗺️',
  'Przestrzeń miejska': '🏙️',
  'Ruch uliczny': '🚦',
  'Kolory': '🎨',
  'Natura i pogoda': '🌳',
  'Przymiotniki': '📏',
  'Boże Narodzenie': '🎄',
  'Wielkanoc': '🐰',
  'Urodziny': '🎂',
  'Przyjęcia': '🎈',
  'Proste zdania - Powitania': '👋',
  'Proste zdania - Ja i Ty': '🙋',
  'Proste zdania - To jest...': '👆',
  'Proste zdania - Mam...': '🎁',
  'Proste zdania - Lubię...': '❤️',
  'Proste zdania - Mogę...': '💪',
  'Proste zdania - Gdzie jest...?': '🔍',
  'Proste zdania - Czynności': '🏃',
  'Proste zdania - Czas': '⏰',
  'Proste zdania - Miejsca': '📍',
  'Proste zdania - Pozycje': '📦',
  'Test 2 - Impreza i rodzina': '🎉',
  'Test 2 - Członkowie rodziny': '👨‍👩‍👧‍👦',
  'Test 2 - Ruchy ciała': '🤸',
  'Test 2 - Umiejętności': '💪',
  'Rozdział 1': '1️⃣',
  'Rozdział 2': '2️⃣',
  'Rozdział 3': '3️⃣',
  'Rozdział 4': '4️⃣',
  'Rozdział 5': '5️⃣',
  'Czas i data - miesiące': '📅',
  'Czas i data - tydzień': '📆',
  'Czas i data - pory roku': '🌸',
  'Czas i data - pory dnia': '🌅',
  'Czas i data - czas': '🕐',
  'Zjawiska pogodowe': '🌦️',
  'Zabawki': '🧸',
  'Gry i zabawy': '🎲',
  'Dom - budowa': '🏠',
};

// Category colors
const categoryColors: Record<string, string> = {
  'Owoce': '#FFC107',
  'Warzywa': '#8BC34A',
  'Jedzenie i napoje': '#FF9800',
  'Czynności - jedzenie': '#FF9800',
  'Zwierzęta domowe': '#FF7043',
  'Zwierzęta dzikie': '#FF5722',
  'Zwierzęta morskie': '#00BCD4',
  'Czynności - zwierzęta': '#FF7043',
  'Rodzina': '#2196F3',
  'Zawody i ludzie': '#3F51B5',
  'Części ciała': '#E91E63',
  'Czynności - własne ciało': '#FF5722',
  'Czynności - inni ludzie': '#9C27B0',
  'Ubrania': '#9C27B0',
  'Ubrania i czynności': '#9C27B0',
  'Transport': '#607D8B',
  'Transport i czynności': '#607D8B',
  'Przedmioty codzienne': '#795548',
  'Dom i wyposażenie': '#795548',
  'Dom i przedmioty': '#795548',
  'Ogród': '#4CAF50',
  'Czynności - dom i ogród': '#8BC34A',
  'Kształty': '#673AB7',
  'Sport': '#F44336',
  'Poruszanie się': '#FF5722',
  'Wykonywanie pracy': '#3F51B5',
  'Geografia - podstawy': '#2196F3',
  'Geografia - trudniejsze': '#1976D2',
  'Przestrzeń miejska': '#607D8B',
  'Ruch uliczny': '#FF9800',
  'Kolory': '#E91E63',
  'Natura i pogoda': '#4CAF50',
  'Przymiotniki': '#009688',
  'Boże Narodzenie': '#C62828',
  'Wielkanoc': '#8BC34A',
  'Urodziny': '#E91E63',
  'Przyjęcia': '#9C27B0',
  'Proste zdania - Powitania': '#42A5F5',
  'Proste zdania - Ja i Ty': '#5C6BC0',
  'Proste zdania - To jest...': '#7E57C2',
  'Proste zdania - Mam...': '#AB47BC',
  'Proste zdania - Lubię...': '#EC407A',
  'Proste zdania - Mogę...': '#EF5350',
  'Proste zdania - Gdzie jest...?': '#FF7043',
  'Proste zdania - Czynności': '#FFA726',
  'Proste zdania - Czas': '#FFCA28',
  'Proste zdania - Miejsca': '#66BB6A',
  'Proste zdania - Pozycje': '#26A69A',
  'Test 2 - Impreza i rodzina': '#E91E63',
  'Test 2 - Członkowie rodziny': '#2196F3',
  'Test 2 - Ruchy ciała': '#FF5722',
  'Test 2 - Umiejętności': '#4CAF50',
  'Czas i data - miesiące': '#5C6BC0',
  'Czas i data - tydzień': '#7986CB',
  'Czas i data - pory roku': '#81C784',
  'Czas i data - pory dnia': '#FFB74D',
  'Czas i data - czas': '#9575CD',
  'Zjawiska pogodowe': '#42A5F5',
  'Zabawki': '#F48FB1',
  'Gry i zabawy': '#7E57C2',
  'Dom - budowa': '#8D6E63',
};

const getStyle = (name: string) => ({
  icon: lessonIcons[name] || '📚',
  color: categoryColors[name] || '#9E9E9E'
});

const defaultStyle = { icon: '📚', color: '#9E9E9E' };

const Games: React.FC = () => {
  const [sets, setSets] = useState<LessonSet[]>([]);
  const [allLessons, setAllLessons] = useState<Lesson[]>([]);
  const [customSets, setCustomSets] = useState<CustomSet[]>([]);
  const [selectedLesson, setSelectedLesson] = useState<string | null>(null);
  const [selectedCustomSet, setSelectedCustomSet] = useState<string | null>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [lessonData, customData] = await Promise.all([
          getLessonSets(),
          getCustomSets().catch(() => []) // Don't fail if custom sets fail
        ]);
        setSets(lessonData);
        setCustomSets(customData);
        // Flatten all lessons
        const lessons = lessonData.flatMap(set => set.lessons || []);
        setAllLessons(lessons);
      } catch (err) {
        console.error('Failed to load lessons:', err);
      }
    };
    loadData();
  }, []);

  // Separate learned (completed) and unlearned lessons
  const learnedLessons = allLessons.filter(l => l.times_completed > 0);

  // Group unlearned lessons by set
  const unlearnedBySet = sets.map(set => ({
    ...set,
    lessons: (set.lessons || []).filter(l => l.times_completed === 0 && (l.word_count > 0 || l.sentence_count > 0))
  })).filter(set => set.lessons.length > 0);

  if (!selectedLesson && !selectedCustomSet) {
    return (
      <div className="games-page fade-in">
        <h1>🎮 Gry</h1>
        <p className="subtitle">Najpierw wybierz kategorię słówek:</p>

        {customSets.length > 0 && (
          <>
            <h3 className="category-section-title">📝 Moje zestawy</h3>
            <div className="category-grid">
              {customSets.map(customSet => (
                <button
                  key={customSet.id}
                  className="category-card custom-set"
                  style={{ '--cat-color': customSet.color } as React.CSSProperties}
                  onClick={() => setSelectedCustomSet(customSet.id)}
                >
                  <span className="category-icon">{customSet.icon}</span>
                  <span className="category-name">{customSet.name}</span>
                  <span className="category-badge">📝</span>
                </button>
              ))}
            </div>
          </>
        )}

        {learnedLessons.length > 0 && (
          <>
            <h3 className="category-section-title">✅ Ukończone lekcje</h3>
            <div className="category-grid">
              {learnedLessons.map(lesson => {
                const style = getStyle(lesson.name_pl);
                return (
                  <button
                    key={lesson.id}
                    className="category-card learned"
                    style={{ '--cat-color': style.color } as React.CSSProperties}
                    onClick={() => setSelectedLesson(lesson.id)}
                  >
                    <span className="category-icon">{style.icon}</span>
                    <span className="category-name">{lesson.name_pl}</span>
                    <span className="category-badge">✓</span>
                  </button>
                );
              })}
            </div>
          </>
        )}

        {unlearnedBySet.length > 0 && (
          <>
            <h3 className="category-section-title new-section">📚 Nowe kategorie</h3>
            {unlearnedBySet.map(set => (
              <div key={set.id} className="category-set-group">
                <h4 className="category-set-heading">{set.icon} {set.name_pl}</h4>
                <div className="category-grid">
                  {set.lessons.map(lesson => {
                    const style = getStyle(lesson.name_pl);
                    return (
                      <button
                        key={lesson.id}
                        className="category-card unlearned"
                        style={{ '--cat-color': style.color } as React.CSSProperties}
                        onClick={() => setSelectedLesson(lesson.id)}
                      >
                        <span className="category-icon">{style.icon}</span>
                        <span className="category-name">{lesson.name_pl}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    );
  }

  // Custom set selected
  if (selectedCustomSet) {
    const selectedCustomSetData = customSets.find(s => s.id === selectedCustomSet);
    return (
      <div className="games-page fade-in">
        <h1>🎮 Gry</h1>

        <div className="selected-category" style={{ '--cat-color': selectedCustomSetData?.color || '#607D8B' } as React.CSSProperties}>
          <span className="selected-icon">{selectedCustomSetData?.icon || '📝'}</span>
          <span className="selected-name">{selectedCustomSetData?.name}</span>
          <button className="change-category-btn" onClick={() => setSelectedCustomSet(null)}>
            Zmień ✕
          </button>
        </div>

        <div className="games-grid">
          {gameModules.map(game => (
            <Link
              key={game.id}
              to={`${game.path}?customSetId=${selectedCustomSet}`}
              className="game-card"
            >
              <span className="game-icon">{game.icon}</span>
              <h3>{game.name}</h3>
              <p>{game.description}</p>
            </Link>
          ))}
        </div>
      </div>
    );
  }

  // Regular lesson selected
  const selectedLessonData = allLessons.find(l => l.id === selectedLesson);
  const style = selectedLessonData ? getStyle(selectedLessonData.name_pl) : defaultStyle;

  return (
    <div className="games-page fade-in">
      <h1>🎮 Gry</h1>

      <div className="selected-category" style={{ '--cat-color': style.color } as React.CSSProperties}>
        <span className="selected-icon">{style.icon}</span>
        <span className="selected-name">{selectedLessonData?.name_pl}</span>
        <button className="change-category-btn" onClick={() => setSelectedLesson(null)}>
          Zmień ✕
        </button>
      </div>

      <div className="games-grid">
        {gameModules.map(game => (
          <Link
            key={game.id}
            to={`${game.path}?lessonId=${selectedLesson}`}
            className="game-card"
          >
            <span className="game-icon">{game.icon}</span>
            <h3>{game.name}</h3>
            <p>{game.description}</p>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default Games;

